import {type ChangeEvent, useCallback, useMemo, memo} from 'react';
import dayjs from 'dayjs';
import {Controller, useFormContext} from 'react-hook-form';
import {useTranslation} from 'react-i18next';
import {type TFunction} from 'i18next';
import {CustomField as CustomFieldType, DATE_FORMAT, FIELD_TYPES} from '@guestapp/sdk';
import {getCustomFieldData} from 'pages/AddPersonalDataForm/utils';
import Datepicker from 'components/common/Datepicker';
import TimePicker from 'components/common/FieldTimePicker';
import FileInput from 'components/common/FieldFileInput';
import Select from 'components/common/Select';
import {InputController as Input} from '@guestapp/ui';
import {FormFieldWrapper} from 'styled/common';
import {createGetRequiredOrOptionalFieldLabel} from './getRequiredOrOptionalFieldLabel';
import {FormTypes} from 'utils/guestForm';

const ValidFileTypes = ['image/jpeg', 'application/pdf', 'image/png'];
const AcceptableDocuments = '.jpg, .png, .pdf, .jpeg';

const validateFileType = (t: TFunction) => (value?: File) => {
  if (!!value && !ValidFileTypes.includes(value.type)) {
    return t('only_doc_file_types');
  }

  return true;
};

type UseCustomFieldProps = {
  disabled?: boolean;
  field: Omit<CustomFieldType, 'id'>;
  required: boolean | string;
};
export const CustomField = memo(({disabled, field, required}: UseCustomFieldProps) => {
  const {t} = useTranslation();
  const {register, control, formState} = useFormContext<FormTypes>();

  const {name, placeholder, label, timeSettings, options} = getCustomFieldData(field);

  const getRequiredOrOptionalFieldLabel = useMemo(
    () => createGetRequiredOrOptionalFieldLabel(t),
    [t],
  );

  const handleFileChange = useCallback(
    (onChange: (value: File | string) => void) =>
      (event: ChangeEvent<HTMLInputElement> | string) => {
        if (!event || typeof event === 'string') {
          return onChange(event);
        }

        const {target} = event;
        if (target?.files?.length) {
          return onChange(target.files[0]);
        }
      },
    [],
  );

  switch (field.field_type) {
    case FIELD_TYPES.date:
      return (
        <FormFieldWrapper>
          <Controller
            name={name}
            rules={{required}}
            control={control}
            render={({field: {onChange}, fieldState: {error}}) => (
              <Datepicker
                label={getRequiredOrOptionalFieldLabel(label, Boolean(required))}
                error={error?.message}
                onChange={value => {
                  return onChange(dayjs(value?.toString()).format(DATE_FORMAT));
                }}
                disabled={disabled}
                {...field}
              />
            )}
          />
        </FormFieldWrapper>
      );
    case FIELD_TYPES.time:
      return (
        <FormFieldWrapper>
          <Controller
            name={name}
            rules={{required}}
            control={control}
            render={({field, fieldState: {error}}) => (
              <TimePicker
                label={getRequiredOrOptionalFieldLabel(label, Boolean(required))}
                error={error?.message}
                disabled={disabled}
                placeholder={label}
                timeSettings={timeSettings}
                {...field}
              />
            )}
          />
        </FormFieldWrapper>
      );
    case FIELD_TYPES.file:
      return (
        <FormFieldWrapper>
          <Controller
            name={name}
            rules={{
              required,
              validate: validateFileType(t),
            }}
            control={control}
            render={({field: {onChange, ...restField}, fieldState: {error}}) => (
              <FileInput
                placeholder={getRequiredOrOptionalFieldLabel(
                  placeholder,
                  Boolean(required),
                )}
                onChange={handleFileChange(onChange)}
                label={getRequiredOrOptionalFieldLabel(label, Boolean(required))}
                error={error?.message}
                accept={AcceptableDocuments}
                disabled={disabled}
                {...restField}
              />
            )}
          />
        </FormFieldWrapper>
      );
    case FIELD_TYPES.text:
      return (
        <FormFieldWrapper>
          <Input
            id={name}
            label={getRequiredOrOptionalFieldLabel(label, Boolean(required))}
            {...register(name, {
              required,
            })}
            control={control}
            error={formState.errors[name]?.message as string}
            disabled={disabled}
          />
        </FormFieldWrapper>
      );

    case FIELD_TYPES.select:
      return (
        <FormFieldWrapper>
          <Controller
            name={name}
            rules={{
              required,
            }}
            control={control}
            render={({field, fieldState: {error}}) => (
              <Select
                label={getRequiredOrOptionalFieldLabel(label, Boolean(required))}
                placeholder={t('select')}
                options={options}
                error={error?.message}
                disabled={disabled}
                {...field}
              />
            )}
          />
        </FormFieldWrapper>
      );
  }

  return null;
});
